// Mecca Majlis certificate — generated client-side, never sees a server.
// Uses two pre-generated assets:
//   /majlis/certificate-bg.png  (landscape parchment with Islamic border)
//   /majlis/seal.png            (circular embossed gold seal)
import { jsPDF } from "jspdf";

const GOLD = "#C8A24B";
const EMERALD = "#0B4D3B";
const CHARCOAL = "#26211B";

const HONORIFIC_LINE_1 = "YA SARKAR NAWAZ ALLAH";
const HONORIFIC_LINE_2 = "HAZRAT ABU MUHAMMAD BADE PEER MUHIYYUL SHAH NAWAZ ALLAH QADIR GHOUS-E-RABBANI";

const HIJRI_MONTHS = [
  "Muharram", "Safar", "Rabi al-Awwal", "Rabi al-Thani",
  "Jumada al-Awwal", "Jumada al-Thani", "Rajab", "Shaban",
  "Ramadan", "Shawwal", "Dhul-Qadah", "Dhul-Hijjah",
];

const toHijri = (d) => {
  try {
    const fmt = new Intl.DateTimeFormat("en-u-ca-islamic-umalqura", {
      year: "numeric", month: "numeric", day: "numeric",
    });
    const parts = fmt.formatToParts(d);
    const get = (t) => parts.find(p => p.type === t)?.value || "";
    const day = get("day");
    const monthNum = parseInt(get("month"), 10);
    const year = String(get("year")).replace(/[^0-9]/g, "");
    return `${day} ${HIJRI_MONTHS[monthNum - 1] || ""} ${year} AH`;
  } catch {
    return "";
  }
};

const gregorianStr = (d) =>
  d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

// Load an image URL into a data URL so jsPDF can embed it.
const loadImageAsDataURL = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      try {
        resolve(canvas.toDataURL("image/png"));
      } catch (e) { reject(e); }
    };
    img.onerror = () => reject(new Error(`Failed to load ${url}`));
    img.src = url;
  });

export const generateCertificate = async ({ name, city, devoteeNumber }) => {
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "landscape" });
  const w = doc.internal.pageSize.getWidth();  // 297
  const h = doc.internal.pageSize.getHeight(); // 210

  // 1. Background image (parchment + Islamic border)
  try {
    const bg = await loadImageAsDataURL("/majlis/certificate-bg.png");
    doc.addImage(bg, "PNG", 0, 0, w, h, undefined, "FAST");
  } catch {
    // Fallback: cream fill if background fails to load
    doc.setFillColor("#F7F1E3");
    doc.rect(0, 0, w, h, "F");
  }

  // The decorated border occupies roughly the outer 18mm + top 25mm crest.
  // Content lives within a safe inner zone (avoid the top crest and bottom corners).
  const innerLeft = 60;
  const innerRight = w - 60;
  const cx = w / 2;

  // 1. "CERTIFICATE OF DEVOTION" — small caps banner just below the top crest
  doc.setTextColor(CHARCOAL);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text("CERTIFICATE OF DEVOTION", cx, 92, { align: "center", charSpace: 2.5 });

  // 2. Thin gold rule
  doc.setDrawColor(GOLD);
  doc.setLineWidth(0.4);
  doc.line(cx - 30, 97, cx + 30, 97);

  // 3. "This is to certify that"
  doc.setFont("times", "italic");
  doc.setFontSize(11);
  doc.setTextColor(CHARCOAL);
  doc.text("This is to certify that", cx, 107, { align: "center" });

  // 4. Devotee name (large serif)
  doc.setFont("times", "bold");
  doc.setFontSize(26);
  doc.setTextColor(EMERALD);
  doc.text(name, cx, 122, { align: "center" });

  // 5. City line (if provided)
  let bodyTop = 133;
  if (city) {
    doc.setFont("times", "italic");
    doc.setFontSize(11);
    doc.setTextColor(CHARCOAL);
    doc.text(`of ${city}`, cx, 131, { align: "center" });
    bodyTop = 140;
  }

  // 6. Body text (compact)
  doc.setFont("times", "normal");
  doc.setFontSize(10);
  doc.setTextColor(CHARCOAL);
  const body = [
    "has been received into the sacred gathering of the Mecca Majlis —",
    "joining hearts in devotion, faith, and remembrance under the blessed guidance of",
  ];
  let y = bodyTop;
  body.forEach((line) => {
    doc.text(line, cx, y, { align: "center" });
    y += 5;
  });

  // 7. Devotee number — LEFT side, in own row above honorific
  const infoY = y + 4;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(CHARCOAL);
  doc.text("DEVOTEE NUMBER", innerLeft + 22, infoY, { charSpace: 1.2, align: "center" });
  doc.setFont("times", "bold");
  doc.setFontSize(13);
  doc.setTextColor(EMERALD);
  doc.text(`#${devoteeNumber.toLocaleString()}`, innerLeft + 22, infoY + 7, { align: "center" });

  // 8. Issued On — RIGHT side, same row as Devotee number
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(CHARCOAL);
  doc.text("ISSUED ON", innerRight - 22, infoY, { align: "center", charSpace: 1.5 });
  doc.setFont("times", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(CHARCOAL);
  doc.text(gregorianStr(new Date()), innerRight - 22, infoY + 6, { align: "center" });
  const hijri = toHijri(new Date());
  if (hijri) {
    doc.setFont("times", "italic");
    doc.setTextColor(GOLD);
    doc.setFontSize(7.5);
    doc.text(hijri, innerRight - 22, infoY + 11, { align: "center" });
  }

  // 9. Honorific in italic gold — centered, two lines BELOW Devotee/Issued
  doc.setFont("times", "italic");
  doc.setFontSize(10);
  doc.setTextColor(GOLD);
  doc.text(HONORIFIC_LINE_1, cx, infoY + 18, { align: "center" });
  doc.setFontSize(9.5);
  doc.text(HONORIFIC_LINE_2, cx, infoY + 24, { align: "center" });

  // 10. Seal at lower-center — below the honorific (account for second honorific line)
  try {
    const seal = await loadImageAsDataURL("/majlis/seal.png");
    const sealSize = 18;
    doc.addImage(seal, "PNG", cx - sealSize / 2, infoY + 28, sealSize, sealSize);
  } catch { /* seal optional */ }

  const safeFirst = (name.split(/\s+/)[0] || "Devotee").replace(/[^A-Za-z0-9]/g, "");
  doc.save(`Mecca-Majlis-Certificate-${safeFirst}.pdf`);
};
